/* AdminClarityCards — Admin Panel section: cohort stats, search students, open any card. */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Search, IdCard, X } from 'lucide-react';
import ClarityCard from './clarity/ClarityCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
    <div className="text-2xl font-black text-gray-900">{value ?? '—'}</div>
    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
  </div>
);

export default function AdminClarityCards() {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCard, setOpenCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);

  const search = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clarity-card/admin/cards`, { params: { q: query } });
      setUsers(r.data.users || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    axios.get(`${API}/clarity-card/admin/stats`).then((r) => setStats(r.data)).catch(() => {});
    search('');
  }, [search]);

  const openUserCard = async (uid) => {
    setCardLoading(true);
    setOpenCard(null);
    try {
      const r = await axios.get(`${API}/clarity-card/admin/card/${uid}`);
      setOpenCard(r.data);
    } catch { /* ignore */ }
    setCardLoading(false);
  };

  return (
    <div data-testid="admin-clarity-cards-section">
      {/* Cohort stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
        <Stat label="Students" value={stats?.students} />
        <Stat label="Cards" value={stats?.cards_created} />
        <Stat label="Stories" value={stats?.stories_answered} />
        <Stat label="Quizzes" value={stats?.quizzes_taken} />
        <Stat label="Badges" value={stats?.badges_earned} />
        <Stat label="Shortlists" value={stats?.careers_shortlisted} />
        <Stat label="AI checks" value={stats?.ai_risk_checks} />
      </div>

      {/* Search */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search(q)}
            placeholder="Search students by name or email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg"
            data-testid="admin-clarity-search-input"
          />
        </div>
        <button onClick={() => search(q)} className="px-4 py-2 bg-gray-900 text-white text-sm font-bold rounded-lg" data-testid="admin-clarity-search-btn">
          Search
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden bg-white max-h-72 overflow-y-auto">
          {users.map((u) => (
            <button key={u.user_id} onClick={() => openUserCard(u.user_id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-teal-50 transition-colors"
              data-testid={`admin-clarity-user-${u.user_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{u.name}</p>
                <p className="text-xs text-gray-500 truncate">{u.email}</p>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700 bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1">
                <IdCard size={10} /> {u.primary_path || 'No path'}
              </span>
            </button>
          ))}
          {users.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No students found.</p>}
        </div>
      )}

      {/* Card detail modal */}
      {(openCard || cardLoading) && (
        <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setOpenCard(null)} data-testid="admin-clarity-card-modal">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpenCard(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-testid="admin-clarity-card-close">
              <X size={18} />
            </button>
            {cardLoading && <div className="py-14 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-teal-600" /></div>}
            {openCard && (
              <>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Full card · {openCard.email}</p>
                <p className="text-xs text-gray-400 mb-5">Share slug: /card/{openCard.slug}</p>
                <ClarityCard card={openCard} />
                {openCard.parent_rows && (
                  <div className="mt-6" data-testid="admin-parent-rows">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Parent view rows (path-adaptive)</p>
                    <div className="space-y-1.5 text-sm">
                      {openCard.parent_rows.map((r) => (
                        <div key={r.label} className="flex justify-between gap-4 border-b border-gray-100 pb-1.5">
                          <span className="text-gray-500 shrink-0">{r.label}</span>
                          <span className="font-bold text-right text-gray-900">{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {openCard.profile_tags_admin && Object.keys(openCard.profile_tags_admin).length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Temperament tags (admin-only, never shown to student)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(openCard.profile_tags_admin).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
                        <span key={t} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100">{t} × {n}</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
